/**
 * Money display for the Ship & Debit pages.
 *
 * The same plain-USD formatter was written out eight times across S&D, each
 * time with its own idea of what an absent value looks like — '—', '-',
 * '$0.00', or nothing at all. The formatting never differed; only the empty
 * rendering did, and that difference is real (a dash in a detail table, a zero
 * where a VDM total of zero is meaningful), so it is a parameter here rather
 * than something to flatten.
 *
 * Two other S&D conventions are deliberately NOT folded in, because they are
 * different presentations rather than copies of this one:
 *   - accounting-style negatives — `$ (1,234.56)` — via formatAccountingCurrency
 *   - the AS/400 green-screen replica's CAD, 2-to-4 decimals
 */

export interface SdCurrencyOptions {
  /** Rendered when the value is null, undefined or ''. Defaults to '—'. */
  empty?: string;
  /** Minimum fraction digits (default 2). */
  decimals?: number;
  /** Maximum fraction digits (defaults to `decimals`). */
  maxDecimals?: number;
  /**
   * What to show when the value is present but not a number. Defaults to
   * `empty`; pass 'raw' to echo the original text, which is what the EDI detail
   * table wants for a field the supplier sent as free text.
   *
   * 'raw' also makes the numeric parse STRICT: the whole string must be a
   * number. Lenient parsing would read `"12 USD"` as 12 and render `$12.00`,
   * presenting an operator reviewing a supplier response with a plausible but
   * invented amount instead of what the supplier actually sent.
   */
  nonNumeric?: 'empty' | 'raw';
}

/**
 * Format a Ship & Debit money value as plain USD.
 *
 * Accepts the shapes these pages actually hold: a number, a decimal string
 * (Sequelize DECIMAL columns arrive as strings over the wire), or nothing.
 */
/**
 * `Intl.NumberFormat` construction is expensive and these formatters render per
 * table cell, so one instance is kept per decimal shape.
 */
const formatterCache = new Map<string, Intl.NumberFormat>();

function formatterFor(
  decimals: number,
  maxDecimals: number,
): Intl.NumberFormat {
  const key = `${decimals}:${maxDecimals}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: maxDecimals,
    });
    formatterCache.set(key, formatter);
  }
  return formatter;
}

export function formatSdCurrency(
  value: number | string | null | undefined,
  options: SdCurrencyOptions = {},
): string {
  const {
    empty = '—',
    decimals = 2,
    maxDecimals = decimals,
    nonNumeric = 'empty',
  } = options;

  if (value === null || value === undefined || value === '') return empty;

  // Strict for 'raw' (see `nonNumeric`); lenient otherwise, which is what the
  // parseFloat-based call sites this replaced did with their own decimal
  // strings.
  const num =
    typeof value === 'string'
      ? nonNumeric === 'raw'
        ? Number(value)
        : parseFloat(value)
      : value;
  // `Infinity` is not NaN but has no sensible currency rendering, and Intl
  // would print "∞". Treat it as unrepresentable.
  if (!Number.isFinite(num)) {
    return nonNumeric === 'raw' ? String(value) : empty;
  }

  return formatterFor(decimals, maxDecimals).format(num);
}
